import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { FileCheck, Target, Search, Edit3, Mail, BarChart2, Languages, ScanText } from 'lucide-react';
export function Features() {
  const {
    language
  } = useLanguage();
  const content = {
    en: {
      title: 'What you get (premium output)',
      features: [{
        title: 'Professional ATS-friendly PDF',
        desc: 'Clean, recruiter-ready formatting designed to be parsed reliably by ATS—without unnecessary design elements that can reduce readability.',
        icon: FileCheck
      }, {
        title: 'Job-matched keywords',
        desc: 'CV Go App identifies relevant terms in the job post and aligns your resume language so your skills are recognized.',
        icon: Target
      }, {
        title: 'Missing keywords',
        desc: 'See which important terms are not represented—so you can make informed, truthful edits.',
        icon: Search
      }, {
        title: 'Clearer, stronger bullets',
        desc: 'Your experience is rewritten for clarity and impact—so it reads better to both ATS and humans.',
        icon: Edit3
      }, {
        title: 'Formal 1-page cover letter',
        desc: 'A concise, professional letter aligned to the role—ready to submit.',
        icon: Mail
      }, {
        title: 'Improvement report',
        desc: 'A clear summary of the enhancements applied to your original resume—so you understand what improved and why.',
        icon: BarChart2
      }, {
        title: 'English/Spanish auto-detect',
        desc: 'CV Go App automatically matches the language of your inputs (EN/ES).',
        icon: Languages
      }, {
        title: 'OCR supported',
        desc: 'If your resume or job description is in a PDF, OCR helps extract text so you can generate results quickly.',
        icon: ScanText
      }]
    },
    es: {
      title: 'Qué obtienes (resultado premium)',
      features: [{
        title: 'PDF profesional compatible con ATS',
        desc: 'Formato limpio y profesional, pensado para que el ATS lo interprete correctamente, sin elementos visuales que reduzcan legibilidad.',
        icon: FileCheck
      }, {
        title: 'Alineación de palabras clave',
        desc: 'CV Go App identifica términos relevantes en la oferta y alinea el lenguaje del CV para que tus habilidades sean reconocidas.',
        icon: Target
      }, {
        title: 'Missing Keywords',
        desc: 'Ves qué términos importantes no aparecen en tu CV, para que puedas hacer ajustes informados (y siempre verídicos).',
        icon: Search
      }, {
        title: 'Bullets más claros y fuertes',
        desc: 'Tu experiencia se presenta con mayor claridad e impacto, mejorando la lectura para ATS y para humanos.',
        icon: Edit3
      }, {
        title: 'Cover letter formal de 1 página',
        desc: 'Una carta breve, profesional y alineada al rol, lista para enviar.',
        icon: Mail
      }, {
        title: 'Reporte de mejoras',
        desc: 'Un resumen claro de los cambios aplicados vs tu CV original, para que entiendas qué mejoró y por qué.',
        icon: BarChart2
      }, {
        title: 'Detección automática EN/ES',
        desc: 'La app detecta automáticamente el idioma de tu input (inglés o español) y genera resultados acordes.',
        icon: Languages
      }, {
        title: 'OCR para PDFs',
        desc: 'Si tu CV u oferta está en PDF, el OCR ayuda a extraer el texto para generar resultados de forma rápida.',
        icon: ScanText
      }]
    }
  };
  const t = content[language];
  return <section className="py-20 bg-gray-50" data-id="element-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25">
        <div className="text-center mb-16" data-id="element-26">
          <h2 className="text-3xl font-bold text-gray-900" data-id="element-27">{t.title}</h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-28">
          {t.features.map((feature, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow border border-gray-100" data-id="element-29">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-4 text-blue-600" data-id="element-30">
                <feature.icon className="w-5 h-5" data-id="element-31" />
              </div>
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-32">{feature.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed" data-id="element-33">
                {feature.desc}
              </p>
            </motion.div>)}
        </div>
      </div>
    </section>;
}